import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import {
  FEATURE_KEYS,
  FeatureFlagsProvider,
  useFeatureFlag,
} from './contexts/FeatureFlagsContext';
import LoginPage from './pages/LoginPage';
import SimulatorPage from './pages/SimulatorPage';
import PortfolioDashboardPage from './pages/portfolio/PortfolioDashboardPage';
import PortfolioLegacyRedirect from './pages/portfolio/PortfolioLegacyRedirect';
import InviteAcceptPage from './pages/InviteAcceptPage';
import OnboardingPage from './pages/onboarding/OnboardingPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProfilePage from './pages/ProfilePage';
import ProtectedRoute from './components/ProtectedRoute';
import Header from './components/Header';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import HomeRedirect from './components/HomeRedirect';
import NotFoundPage from './pages/NotFoundPage';
import AccessDeniedPage from './pages/AccessDeniedPage';

const InvestorGate = lazy(() => import('./pages/investor/InvestorGate'));
const InvestorPage = lazy(() => import('./pages/investor/InvestorPage'));
const ExplainerPage = lazy(() => import('./pages/investor/explainer/ExplainerPage'));
import { usePageAnalytics } from './reporting/usePageAnalytics';
import { APP_ROUTE_TITLE_RULES } from './routeTitles';
import { useAppRoutePageTitle } from './useAppRoutePageTitle';

function FeatureUnavailable({ label }: { label: string }) {
  return <AccessDeniedPage featureName={label} />;
}

function GatedSimulatorPage() {
  const enabled = useFeatureFlag(FEATURE_KEYS.simulatorMaster);
  if (!enabled) return <FeatureUnavailable label="Simulator" />;
  return <SimulatorPage />;
}

function AppShell() {
  return (
    <div className="relative flex min-h-screen w-full flex-col bg-[color:var(--nq-bg)] bg-fixed bg-[image:radial-gradient(ellipse_70%_45%_at_50%_-10%,rgba(37,180,224,0.13),transparent),radial-gradient(ellipse_55%_40%_at_100%_30%,rgba(247,148,29,0.07),transparent)] before:pointer-events-none before:fixed before:inset-0 before:z-0 before:bg-[url('/assets/logo-bull.png')] before:bg-[length:min(40vw,778px)_auto] before:bg-fixed before:bg-center before:bg-no-repeat before:opacity-[0.08] before:content-['']">
      <Header />
      <main className="relative z-[1] flex min-h-[calc(100vh-var(--app-header-height)-48px)] w-full flex-1 flex-col">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

function AppRoutes() {
  const { user } = useAuth();
  const location = useLocation();
  useAppRoutePageTitle(APP_ROUTE_TITLE_RULES);
  usePageAnalytics({
    // /investor and its sub-pages emit their own screen-scoped analytics.
    enabled: !location.pathname.startsWith('/investor'),
  });

  return (
    <Routes>
      <Route
        path="/investor"
        element={
          <Suspense
            fallback={
              <div
                style={{
                  minHeight: '100vh',
                  display: 'grid',
                  placeItems: 'center',
                  color: 'var(--color-medium-gray-text)',
                }}
              >
                Loading investor page…
              </div>
            }
          >
            <InvestorGate>
              <InvestorPage />
            </InvestorGate>
          </Suspense>
        }
      />

      <Route
        path="/investor/how-novaquantis-works"
        element={
          <Suspense
            fallback={
              <div
                style={{
                  minHeight: '100vh',
                  display: 'grid',
                  placeItems: 'center',
                  color: 'var(--color-medium-gray-text)',
                }}
              >
                Loading…
              </div>
            }
          >
            <InvestorGate>
              <ExplainerPage />
            </InvestorGate>
          </Suspense>
        }
      />

      <Route
        path="/investor/how-novaquantis-ria-works"
        element={<Navigate to="/investor/how-novaquantis-works" replace />}
      />

      <Route element={<AppShell />}>
        <Route
          path="/login"
          element={user ? <Navigate to="/" replace /> : <LoginPage />}
        />
        <Route
          path="/simulator"
          element={
            <ProtectedRoute>
              <GatedSimulatorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolio"
          element={
            <ProtectedRoute>
              <PortfolioDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolio/:accountId"
          element={
            <ProtectedRoute>
              <PortfolioLegacyRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile/*"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route path="/user/invite/:hash" element={<InviteAcceptPage />} />
        <Route
          path="/reset-password/:token"
          element={<ResetPasswordPage />}
        />
        <Route path="/" element={<HomeRedirect />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <FeatureFlagsProvider>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 3000,
              style: {
                background: '#1e293b',
                color: '#f1f5f9',
                fontSize: '14px',
              },
              success: { iconTheme: { primary: '#22c55e', secondary: '#fff' } },
              error: {
                iconTheme: { primary: '#ef4444', secondary: '#fff' },
                duration: 5000,
              },
            }}
          />
          <AppRoutes />
        </FeatureFlagsProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
